import { BindingEngine, computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { Claims } from "../../../../../../constants/authentication/claims";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../../models/basics/basics.models";
import { RightShipInspectionInvoiceModel, RightShipInspectionClientModel, RightshipInspectionBillingStatusEnum, RightShipInspectionStatusEnum } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { UnitOfWorkObserver } from "../../../../../../utilities/unit-of-work-observer";
import { Reflection } from "@rightship-tech/common-ui/helpers/reflection";
import moment from 'moment';
import { ValidationController, ValidationControllerFactory, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { LookupValue } from "@rightship-tech/common-ui/models/lookup.models";

@autoinject
@transient()
export class EditRSInspectionBillingDetailsDialog {

    public selectCancelledInspectionPenalty: boolean = false;
    public selectLateInspectionPenalty: boolean = false;
    public selectOtherPenaltyAmount: boolean = false;
    public readonly format: string = "DD MMM YYYY";
    public currency: string;
    public selectedItem: LookupValue

    originalInvoice: RightShipInspectionInvoiceModel;
    originalItem: RightShipInspectionClientModel;
    invoice: RightShipInspectionInvoiceModel;
    rightShipInspectionId: number;
    billingCustomer: any;
    hasLegacyInvoice: boolean;
    private _uow: UnitOfWorkObserver<RightShipInspectionInvoiceModel>;
    private _validator: ValidationController;

    canEditBillingDetailsCurrencyAmountSales: boolean;

    billingCustomerEntityType: EntityTypeConfigurationModel[];
    generatedInvoiceNumber: string;
    constructor(private dialogClientService: DialogClientService, private bindingEngine: BindingEngine,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper, private rightshipInspectionService: RightShipInspectionsService,
        private notificationService: NotificationClientService, private userClaimService: UserClaimsService, validationControllerFactory: ValidationControllerFactory) {
        this.canEditBillingDetailsCurrencyAmountSales = this.userClaimService.hasPermission(Claims.CanEditBillingDetailsCurrencyAmountSales);
        this.billingCustomerEntityType = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["BillingAccounts"]);
        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
    }

    async activate(model: { rightShipInspectionId: number, invoice: RightShipInspectionInvoiceModel, item: RightShipInspectionClientModel }) {
        if (!model) return;
        this.rightShipInspectionId = model.rightShipInspectionId;
        this.generatedInvoiceNumber = `${model.item.inspectionInvoiceNumberPrefix}${this.rightShipInspectionId}`;
        this.originalInvoice = model.invoice;
        this.originalItem = model.item;
        this.invoice = CloneHelper.cloneObject(model.invoice);

        if (this.invoice) {
            if (this.invoice.cancelledInspectionPenalty > 0) this.selectCancelledInspectionPenalty = true;
            if (this.invoice.lateInspectionPenalty > 0) this.selectLateInspectionPenalty = true;
            if (this.invoice.otherPenaltyAmount > 0) this.selectOtherPenaltyAmount = true;
            this.currency = this.invoice.supplierInvoiceCurrency;
        }

        this.initModels();
        if (!this.invoice.rightShipInspectionId)
            this.invoice.rightShipInspectionId = this.rightShipInspectionId;
    }

    cancel() {
        this.dialogClientService.hide(true);
    }

    initModels() {

        if (!this.invoice) {
            this.invoice = {
                inspectionMarkup: 0,
                invoiceReceivedDate: null,
                address: null,
                billingCustomerId: null,
                city: null,
                companyName: null,
                contactName: null,
                countryId: null,
                email: null,
                additionalEmailsToNotify: null,
                invoiceNumber: null,
                generatedInvoiceNumber: this.generatedInvoiceNumber,
                invoiceStatusId: null,
                phoneNumber: null,
                postCode: null,
                billingInstructions: null,
                state: null,
                billingCustomer: null,
                invoiceStatus: null,
                rightShipInspectionId: null,
                supplierInspectionAmount: 0,
                supplierTravelAndExpenseAmount: 0,
                legacySupplierInvoiceTotal: null,
                supplierInvoiceCurrency: null,
                billingAccountId: null,
                cancelledInspectionPenalty: 0,
                lateInspectionPenalty: 0,
                otherPenaltyAmount: 0,
                totalSupplierInvoiceAmount: 0,
                currencyConversionRate: 0
            };
        }
        else {
            this.invoice.rightShipInspectionId = this.rightShipInspectionId;

            this.hasLegacyInvoice = !!this.invoice.legacySupplierInvoiceTotal;
        }

        this._uow = new UnitOfWorkObserver<RightShipInspectionInvoiceModel>(this.bindingEngine, this.invoice);
        this._uow.startTracking();

    }
    // Total supplier invoice amount (USD)
    @computedFrom(Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.supplierTravelAndExpenseAmount),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.supplierInvoiceCurrency),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.supplierTravelAndExpenseAmountUSD),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.currencyConversionRate),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.supplierInspectionAmount),
        Reflection.getPropertyName<EditRSInspectionBillingDetailsDialog>(z => z.selectedItem))
    get totalSupplierInvoiceAmountUSD(): number {
        if (this.totalInvoiceAmount > 0) {
            if (this.selectedItem && this.selectedItem.additionalData) {
                this.invoice.totalSupplierInvoiceAmountUSD = this.totalInvoiceAmount * this.selectedItem.additionalData.rate;
                this.invoice.supplierTravelAndExpenseAmountUSD = this.invoice.supplierTravelAndExpenseAmount * this.selectedItem.additionalData.rate;
            }
            return this.invoice.totalSupplierInvoiceAmountUSD;
        }
        return 0;
    }
    // Total amount to be billed to customer
    @computedFrom(Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.totalSupplierInvoiceAmount),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.inspectionMarkup),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.totalPenaltyAmount),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.supplierTravelAndExpenseAmount),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.supplierInvoiceCurrency),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.supplierInspectionAmount),
        Reflection.getPropertyName<EditRSInspectionBillingDetailsDialog>(z => z.selectedItem))
    get totalAmount(): number {
        let result: number = 0;
        if (this.invoice) {
            if (this.totalSupplierInvoiceAmountUSD > 0) result += this.totalSupplierInvoiceAmountUSD;
            if (this.invoice.inspectionMarkup > 0) result += this.invoice.inspectionMarkup;
        }
        if (this.totalPenaltyAmount > 0) result += this.totalPenaltyAmount;
        return result;
    }

    // Total penalty amount
    @computedFrom(Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.selectCancelledInspectionPenalty),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.selectLateInspectionPenalty),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.selectOtherPenaltyAmount),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.cancelledInspectionPenalty),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.lateInspectionPenalty),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.otherPenaltyAmount))
    get totalPenaltyAmount(): number {
        let result: number = 0;
        if (this.invoice) {
            if (this.invoice.cancelledInspectionPenalty > 0 && this.selectCancelledInspectionPenalty) result += this.invoice.cancelledInspectionPenalty;
            if (this.invoice.lateInspectionPenalty > 0 && this.selectLateInspectionPenalty) result += this.invoice.lateInspectionPenalty;
            if (this.invoice.otherPenaltyAmount > 0 && this.selectOtherPenaltyAmount) result += this.invoice.otherPenaltyAmount;
        }
        return result;
    }

    // Total invoice amount
    @computedFrom(Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.supplierInspectionAmount),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.supplierTravelAndExpenseAmount))
    get totalInvoiceAmount(): number {
        let result: number = 0;
        if (this.invoice) {
            let inspectionCost = 0;
            let travelCost = 0;
            if (this.invoice.supplierInspectionAmount > 0) inspectionCost = +(this.invoice.supplierInspectionAmount) || 0;
            if (this.invoice.supplierTravelAndExpenseAmount > 0) travelCost = +(this.invoice.supplierTravelAndExpenseAmount) || 0;
            result = inspectionCost + travelCost;
            this.invoice.totalSupplierInvoiceAmount = result;
        }
        return result;
    }

    // Total amount to be billed to customer
    @computedFrom(Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.totalSupplierInvoiceAmountUSD),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.inspectionMarkup),
        Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.billingCustomer))
    get shouldEnableSaveAndSubmitToSF(): boolean {
        return this.billingCustomer && this.billingCustomer.name.toUpperCase() !== 'UNKNOWN' &&
            this.totalSupplierInvoiceAmountUSD > 0;
    }

    // Disable SaveAndSubmitToSF if inspection markup is invalid
    @computedFrom(Reflection.getPropertyPath<EditRSInspectionBillingDetailsDialog>(z => z.invoice.inspectionMarkup))
    get shouldDisableButton(): boolean {
        return (!this.invoice.inspectionMarkup && this.invoice.inspectionMarkup !== 0) || this.invoice.inspectionMarkup < 0;
    }

    // Clear value if checkbox is unchecked
    clearValue(value: boolean, field: string) {
        switch (field) {
            case "cancel-penalty":
                if (!value) this.invoice.cancelledInspectionPenalty = 0;
                break;
            case "late-penalty":
                if (!value) this.invoice.lateInspectionPenalty = 0;
                break;
            case "other-penalty":
                if (!value) this.invoice.otherPenaltyAmount = 0;
                break;
        }
    }

    setUpValidation() {
        this.getRules();
    }
    invoiceReceivedMaxDate() {
        return this.today();
    }
    getRules() {
        ValidationRules
            .ensure("currency")
            .required()
            .withMessage("Please choose one currency")
            .on(this);

        ValidationRules
            .ensure<RightShipInspectionInvoiceModel, any>(x => x.invoiceReceivedDate)
            .required()
            .withMessage("Please choose date")
            .on(this.invoice);
    }

    async save(submitToSF: boolean = false) {
        if (submitToSF) {
            this.setUpValidation();
        }
        const validator = await this._validator.validate();
        if (validator.valid || !submitToSF) {
            if (!this.invoice.inspectionMarkup && this.invoice.inspectionMarkup !== 0 && this.invoice.inspectionMarkup !== this.originalInvoice.inspectionMarkup) {
                this.invoice.inspectionMarkup = null;
                this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.inspectionMarkup), this.invoice.inspectionMarkup, this.originalInvoice.inspectionMarkup);
            }

            // Add changes to UOW, convert string to number for numeric fields, convert date to string in order to compare precisely with the comp-date-picker
            this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.invoiceReceivedDate),
                this.invoice.invoiceReceivedDate?.toISOString(), this.originalInvoice.invoiceReceivedDate);
            this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.supplierInspectionAmount),
                +(this.invoice.supplierInspectionAmount), this.originalInvoice.supplierInspectionAmount);
            this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.supplierTravelAndExpenseAmount),
                +(this.invoice.supplierTravelAndExpenseAmount), this.originalInvoice.supplierTravelAndExpenseAmount);
            this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.supplierInvoiceCurrency),
                this.currency, this.originalInvoice.supplierInvoiceCurrency);

            this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.salesAmount), this.totalAmount, this.originalInvoice.salesAmount);
            this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.salesCurrency), 'USD', this.originalInvoice.salesCurrency);
            this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.invoiceNumber), this.generatedInvoiceNumber, this.originalInvoice.invoiceNumber);

            this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.totalSupplierInvoiceAmountUSD), this.totalSupplierInvoiceAmountUSD, null);

            if (this.selectedItem) {
                this._uow.addChange(Reflection.getPropertyPath<RightShipInspectionInvoiceModel>(z => z.currencyConversionRateId), this.selectedItem.id, null);
                this.invoice.currencyConversionRate = this.selectedItem.additionalData.rate;
            }

            if (this._uow.hasChanges() || submitToSF) {
                //Check if inspection status is new and billing account is change from null to valid billing account, open booking request prompt dialog
                if (this.originalItem.rightShipInspectionStatusId == RightShipInspectionStatusEnum.NewInspection &&
                    (!this.originalInvoice.billingAccountId && this.billingCustomer && this.billingCustomer.name !== 'UNKNOWN') && !submitToSF) {
                    this.dialogClientService.show(RightShipDialogServiceConstants.URLs.BookingRequestsPromptDialog, {
                        invoiceId: this.invoice.rightShipInspectionInvoiceId,
                        changes: this._uow.getChanges(),
                        submitToSF: submitToSF,
                        onProceed: () => {
                            this.applyChanges(submitToSF);
                        }
                    }, false, false, false)
                }
                else {
                    try {
                        await this.rightshipInspectionService.saveRightShipInspectionBillingDetails({
                            id: this.invoice.rightShipInspectionInvoiceId,
                            changes: this._uow.getChanges(),
                            submitOrderToSalesforce: submitToSF
                        });

                        this.notificationService.show("Update successful", "Inspection billing details updated successfully.", NotificationType.Success);

                        this.applyChanges(submitToSF);

                        this.dialogClientService.hide(true);
                    }
                    catch (e) {
                        this.notificationService.show("Failed", "Failed to update inspection billing details.", NotificationType.Error);
                        console.error(e);
                    }
                }
            }
            else {
                this.notificationService.show("No change", "No changes in billing details.", NotificationType.Info);

                this.dialogClientService.hide(true);
            }
        }
    }

    applyChanges(submitToSF: boolean = false) {
        if (this.billingCustomer) {
            this.originalInvoice.billingCustomer = this.billingCustomer.name;
            this.originalInvoice.billingCustomerCode = this.billingCustomer.code;
            this.originalInvoice.billingAccount = this.billingCustomer.name;
        }
        if (this.originalInvoice && this.invoice) {
            this.originalInvoice.billingCustomerId = this.invoice.billingCustomerId;
            this.originalInvoice.billingInstructions = this.invoice.billingInstructions;
            this.originalInvoice.invoiceReceivedDate = this.invoice.invoiceReceivedDate;
            this.originalInvoice.supplierInvoiceCurrency = this.invoice.supplierInvoiceCurrency;
            this.originalInvoice.legacySupplierInvoiceTotal = this.invoice.legacySupplierInvoiceTotal;
            this.originalInvoice.invoiceNumber = this.invoice.invoiceNumber;
            this.originalInvoice.supplierInspectionAmount = this.invoice.supplierInspectionAmount;
            this.originalInvoice.supplierTravelAndExpenseAmount = this.invoice.supplierTravelAndExpenseAmount;
            this.originalInvoice.billingAccountId = this.invoice.billingAccountId;
            this.originalInvoice.inspectionMarkup = this.invoice.inspectionMarkup;
            this.originalInvoice.totalSupplierInvoiceAmount = this.invoice.totalSupplierInvoiceAmount;
            this.originalInvoice.cancelledInspectionPenalty = this.invoice.cancelledInspectionPenalty;
            this.originalInvoice.lateInspectionPenalty = this.invoice.lateInspectionPenalty;
            this.originalInvoice.otherPenaltyAmount = this.invoice.otherPenaltyAmount;
            this.originalInvoice.salesAmount = this.totalAmount;
            this.originalInvoice.salesCurrency = 'USD';
            this.originalInvoice.currencyConversionRate = this.invoice.currencyConversionRate;
            this.originalInvoice.totalSupplierInvoiceAmountUSD = this.invoice.totalSupplierInvoiceAmountUSD;
            if (submitToSF) {
                this.originalItem.billingStatus = "Sales order initiated";
                this.originalItem.billingStatusId = RightshipInspectionBillingStatusEnum.SalesOrderInitiated;
            }
        }
    }

    formattedDate(date: string | Date) {
        return date ? moment(date).format("DD MMM YYYY") : "";
    }

    today() {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return today;
    }

    detached() {
        if (this._uow) {
            this._uow.dispose();
        }
    }
}
